import { useNavigate } from 'react-router-dom'

import { Button, useToggle } from '@factorialco/deprecated-design-system'
import { useAiChat } from '@factorialco/f0-react'
import {
  DeletePostDocument,
  PostDataFragment,
  SetPostCommentsAndReactionsDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { buildInsightsPrompt } from 'modules/posts/lib/buildInsightsPrompt'

interface Props {
  post: PostDataFragment
}

const PostActions = ({ post }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [open, toggle] = useToggle(false)
  const { enabled: aiEnabled, sendMessage, setOpen: setAiChatOpen } = useAiChat()
  const isKudos = post.type === 'Posts::V2::Kudo'
  const toggleInteractions = post.allowCommentsAndReactions ? 'off' : 'on'
  const { has_dev_communities_post_insights } = useFeatures(['dev_communities_post_insights'])

  const [deletePost] = useMutation(DeletePostDocument, {
    variables: {
      postId: post.id,
    },
    extractorFn: (data) => data.postsMutations.deletePostsPost,
  })

  const [setPostCommentsAndReactionsDocument] = useMutation(SetPostCommentsAndReactionsDocument, {
    extractorFn: (data) => data.postsMutations.updatePostsPost,
  })

  const toggleCommentsAndReactions = () => {
    const newAllowCommentsAndReactions = !post.allowCommentsAndReactions
    setPostCommentsAndReactionsDocument({
      variables: {
        postId: post.id,
        postGroupId: post.group?.id,
        allowCommentsAndReactions: newAllowCommentsAndReactions,
      },
      optimisticResponse: {
        postsMutations: {
          updatePostsPost: {
            post: {
              ...post,
              allowCommentsAndReactions: newAllowCommentsAndReactions,
            },
            errors: [],
          },
        },
      },
    })
  }

  const onDeletePost = async () => {
    await deletePost({
      update: (cache) => {
        cache.evict({ id: cache.identify(post) })
      },
    })
    const isFromCommunities = location.pathname.includes(navigation.communities.path)
    navigate(
      isFromCommunities ? navigation.communities.fullPath() : navigation.dashboard.fullPath()
    )
  }

  const toggleLabel =
    toggleInteractions === 'off'
      ? i18n.t('posts.communities.post.toggle_comments_and_reactions_off')
      : i18n.t('posts.communities.post.toggle_comments_and_reactions_on')

  const handleOpenOneChat = () => {
    if (!aiEnabled) return

    const prompt = buildInsightsPrompt({
      postId: post.id,
      title: post.title,
      communityTitle: post.group?.title,
      hasRequiredActions: (post.postActionsConnection?.edges?.length ?? 0) > 0,
    })

    setAiChatOpen(true)
    sendMessage(prompt)
  }

  return (
    <>
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Button.Decorative
            type="secondary"
            iconName="ellipsis"
            aria-label="This button holds the actions of a post"
          />
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="center">
          <PopoverMenu.RouterOption
            title={i18n.t('posts.communities.post.edit')}
            to={{
              pathname: (() => {
                const isFromCommunities = location.pathname.includes(navigation.communities.path)
                if (isKudos) {
                  return navigation.dashboard.kudos.id.edit.fullPath({ id: post.id })
                }
                if (isFromCommunities) {
                  return navigation.communities.post.id.edit.fullPath({ id: post.id })
                }
                return navigation.dashboard.post.id.edit.fullPath({ id: post.id })
              })(),
            }}
          />
          <PopoverMenu.TouchableOption
            onClick={() => toggleCommentsAndReactions()}
            title={toggleLabel}
          />
          {has_dev_communities_post_insights && post._policies?.manage?.can && aiEnabled && (
            <PopoverMenu.TouchableOption
              title={i18n.t('posts.communities.insights.breadcrumb')}
              onClick={handleOpenOneChat}
            />
          )}
          <PopoverMenu.TouchableOption
            title={i18n.t('posts.communities.post.delete')}
            danger
            onClick={toggle(true)}
          />
        </PopoverMenu.Content>
      </PopoverMenu>
      {open && (
        <Modal
          illustration="alert"
          title={i18n.t('posts.communities.post.delete')}
          description={i18n.t('posts.communities.post.delete_description')}
          onClose={toggle(false)}
          disableFocusTrap // TODO: if this is enabled it hangs the whole application
          footer={
            <>
              <Button type="secondary" onClick={toggle(false)}>
                {i18n.t('common.cancel')}
              </Button>
              <Button type="primary" onClick={() => onDeletePost()}>
                {i18n.t('posts.communities.post.delete_submit')}
              </Button>
            </>
          }
        />
      )}
    </>
  )
}

export default observer(PostActions)
